import { takeLatest, put, call } from "redux-saga/effects";
import {
  POWERBI_SECURITY_DATA,
  POWERBI_SECURITY_GET_API,
  POWERBI_SECURITY_GET_DATA,
  POWERBI_SECURITY_GET_DETAIL_API,
  POWERBI_SECURITY_ADD_DATA,
  POWERBI_SECURITY_ADD_POST_API,
  POWERBI_SECURITY_UPDATE_DATA,
  POWERBI_SECURITY_UPDATE_PUT_API,
  POWERBI_SECURITY_DELETE_DATA,
  POWERBI_SECURITY_DELETE_API,
  POWERBI_ROLES_DATA,
  POWERBI_ROLES_GET_API,
  POWERBI_SECURITY_UPDATE_SEQUENCE,
  POWERBI_SECURITY_UPDATE_SEQUENCE_API,
  POWERBI_SECURITY_SYNC_PAGES,
  POWERBI_SECURITY_SYNC_PAGES_API,
  POWERBI_SECURITY_GET_REPORT_PAGES,
  POWERBI_SECURITY_GET_REPORT_PAGES_API,
} from "./Constants";
import {
  setPowerBISecurityDataSuccess,
  setPowerBISecurityDataFail,
  setPowerBISecurityGetDataSuccess,
  setPowerBISecurityGetDataFail,
  setPowerBISecurityAddDataSuccess,
  setPowerBISecurityAddDataFail,
  setPowerBISecurityUpdateDataSuccess,
  setPowerBISecurityUpdateDataFail,
  setPowerBISecurityDeleteDataSuccess,
  setPowerBISecurityDeleteDataFail,
  loadPowerBISecurityDataRequest,
  setPowerBIRolesDataSuccess,
  setPowerBIRolesDataFail,
  setPowerBISecurityUpdateSequenceSuccess,
  setPowerBISecurityUpdateSequenceFail,
  setPowerBISecuritySyncPagesSuccess,
  setPowerBISecuritySyncPagesFail,
  setPowerBISecurityGetReportPagesSuccess,
  setPowerBISecurityGetReportPagesFail,
} from "./Actions";
import { reportApi } from "../../../shared/components/Service";
import { addErrorAlert, addSuccessAlert } from "../../alert/Actions";

// ==================== Get All Security Configurations ====================
function* getPowerBISecurityDataService() {
  try {
    const response = yield call(reportApi.get, POWERBI_SECURITY_GET_API);
    const status = response.status;

    if (status === 200) {
      const responseData = response.data;
      // Keep the data as-is with group_role array structure
      const transformedData = responseData.data?.map(item => ({
        ...item,
        group_role: item.group_role || [],
      })) || [];

      yield put(setPowerBISecurityDataSuccess(transformedData));
    } else {
      yield put(addErrorAlert("Failed to load security configurations"));
      yield put(setPowerBISecurityDataFail(response.response?.data || "Error loading security configurations"));
    }
  } catch (error) {
    yield put(addErrorAlert("Error loading security configurations"));
    yield put(setPowerBISecurityDataFail(error));
  }
}

// ==================== Get Single Security Configuration ====================
function* getPowerBISecurityGetDataService({ id }) {
  try {
    const response = yield call(
      reportApi.getDetail,
      POWERBI_SECURITY_GET_DETAIL_API + id
    );
    const status = response.status;

    if (status === 200) {
      const data = response.data;
      yield put(setPowerBISecurityGetDataSuccess(data));
    } else {
      yield put(addErrorAlert("Failed to load security configuration"));
      yield put(setPowerBISecurityGetDataFail(response.response?.data || "Error loading security configuration"));
    }
  } catch (error) {
    yield put(addErrorAlert("Error loading security configuration"));
    yield put(setPowerBISecurityGetDataFail(error));
  }
}

// ==================== Add Security Configuration ====================
function* postPowerBISecurityAddDataService({ data }) {
  try {
    const response = yield call(reportApi.post, POWERBI_SECURITY_ADD_POST_API, data);
    const status = response.status;

    if (status === 200 || status === 201) {
      yield put(addSuccessAlert("Security configuration added successfully"));
      yield put(setPowerBISecurityAddDataSuccess(response.data));
      yield put(loadPowerBISecurityDataRequest());
    } else {
      yield put(addErrorAlert("Failed to add security configuration"));
      yield put(setPowerBISecurityAddDataFail(response.response?.data || "Error adding security configuration"));
    }
  } catch (error) {
    yield put(addErrorAlert("Error adding security configuration"));
    yield put(setPowerBISecurityAddDataFail(error));
  }
}

// ==================== Update Security Configuration ====================
function* putPowerBISecurityUpdateDataService({ id, data }) {
  try {
    const response = yield call(
      reportApi.put,
      POWERBI_SECURITY_UPDATE_PUT_API + id,
      data
    );
    const status = response.status;

    if (status === 200) {
      yield put(addSuccessAlert("Security configuration updated successfully"));
      yield put(setPowerBISecurityUpdateDataSuccess(response.data));
      yield put(loadPowerBISecurityDataRequest());
    } else {
      yield put(addErrorAlert("Failed to update security configuration"));
      yield put(setPowerBISecurityUpdateDataFail(response.response?.data || "Error updating security configuration"));
    }
  } catch (error) {
    yield put(addErrorAlert("Error updating security configuration"));
    yield put(setPowerBISecurityUpdateDataFail(error));
  }
}

// ==================== Delete Security Configuration ====================
function* deletePowerBISecurityDataService({ id }) {
  try {
    const response = yield call(
      reportApi.delete,
      POWERBI_SECURITY_DELETE_API + id
    );
    const status = response.status;

    if (status === 200 || status === 204) {
      yield put(addSuccessAlert("Security configuration deleted successfully"));
      yield put(setPowerBISecurityDeleteDataSuccess());
      yield put(loadPowerBISecurityDataRequest());
    } else {
      yield put(addErrorAlert("Failed to delete security configuration"));
      yield put(setPowerBISecurityDeleteDataFail(response.response?.data || "Error deleting security configuration"));
    }
  } catch (error) {
    yield put(addErrorAlert("Error deleting security configuration"));
    yield put(setPowerBISecurityDeleteDataFail(error));
  }
}

// ==================== Get PowerBI Roles ====================
function* getPowerBIRolesDataService() {
  try {
    const response = yield call(reportApi.get, POWERBI_ROLES_GET_API);
    const status = response.status;

    if (status === 200) {
      const responseData = response.data;
      yield put(setPowerBIRolesDataSuccess(responseData.data || []));
    } else {
      yield put(addErrorAlert("Failed to load PowerBI roles"));
      yield put(setPowerBIRolesDataFail(response.response?.data || "Error loading roles"));
    }
  } catch (error) {
    yield put(addErrorAlert("Error loading PowerBI roles"));
    yield put(setPowerBIRolesDataFail(error));
  }
}

// ==================== Update Sequence Order ====================
function* putPowerBISecurityUpdateSequenceService({ updates }) {
  try {
    const response = yield call(
      reportApi.put,
      POWERBI_SECURITY_UPDATE_SEQUENCE_API,
      updates
    );
    const status = response.status;

    if (status === 200) {
      yield put(setPowerBISecurityUpdateSequenceSuccess());
      // Reload the list to get updated sequence
      yield put(loadPowerBISecurityDataRequest());
    } else {
      yield put(addErrorAlert("Failed to update sequence order"));
      yield put(setPowerBISecurityUpdateSequenceFail(response.response?.data || "Error updating sequence"));
    }
  } catch (error) {
    yield put(addErrorAlert("Error updating sequence order"));
    yield put(setPowerBISecurityUpdateSequenceFail(error));
  }
}

// ==================== Sync Pages ====================
function* postPowerBISecuritySyncPagesService(action) {
  try {
    const { reportId, registryId, pages } = action;
    const response = yield call(
      reportApi.post,
      POWERBI_SECURITY_SYNC_PAGES_API,
      {
        reportId,
        registryId,
        pages
      }
    );
    const status = response.status;

    if (status === 200) {
      yield put(setPowerBISecuritySyncPagesSuccess());
      yield put(addSuccessAlert("Pages synced successfully!"));
      // Reload the list to get updated pages
      yield put(loadPowerBISecurityDataRequest());
    } else {
      yield put(addErrorAlert("Failed to sync pages"));
      yield put(setPowerBISecuritySyncPagesFail(response.response?.data || "Error syncing pages"));
    }
  } catch (error) {
    yield put(addErrorAlert("Error syncing pages"));
    yield put(setPowerBISecuritySyncPagesFail(error));
  }
}

// ==================== Get Report Pages ====================
function* getPowerBISecurityReportPagesService(action) {
  try {
    const { reportId } = action;
    const response = yield call(
      reportApi.get,
      `${POWERBI_SECURITY_GET_REPORT_PAGES_API}/${reportId}`
    );
    const status = response.status;

    if (status === 200) {
      const responseData = response.data;
      yield put(setPowerBISecurityGetReportPagesSuccess(responseData.data || []));
    } else {
      yield put(addErrorAlert("Failed to load report pages"));
      yield put(setPowerBISecurityGetReportPagesFail(response.response?.data || "Error loading report pages"));
    }
  } catch (error) {
    yield put(addErrorAlert("Error loading report pages"));
    yield put(setPowerBISecurityGetReportPagesFail(error));
  }
}

// ==================== Watcher Saga ====================
export function* watchPowerBISecurity() {
  yield takeLatest(
    POWERBI_SECURITY_DATA.POWERBI_SECURITY_DATA_LOAD,
    getPowerBISecurityDataService
  );
  yield takeLatest(
    POWERBI_SECURITY_GET_DATA.POWERBI_SECURITY_GET_DATA_LOAD,
    getPowerBISecurityGetDataService
  );
  yield takeLatest(
    POWERBI_SECURITY_ADD_DATA.POWERBI_SECURITY_ADD_DATA_LOAD,
    postPowerBISecurityAddDataService
  );
  yield takeLatest(
    POWERBI_SECURITY_UPDATE_DATA.POWERBI_SECURITY_UPDATE_DATA_LOAD,
    putPowerBISecurityUpdateDataService
  );
  yield takeLatest(
    POWERBI_SECURITY_DELETE_DATA.POWERBI_SECURITY_DELETE_DATA_LOAD,
    deletePowerBISecurityDataService
  );
  yield takeLatest(
    POWERBI_ROLES_DATA.POWERBI_ROLES_DATA_LOAD,
    getPowerBIRolesDataService
  );
  yield takeLatest(
    POWERBI_SECURITY_UPDATE_SEQUENCE.POWERBI_SECURITY_UPDATE_SEQUENCE_LOAD,
    putPowerBISecurityUpdateSequenceService
  );
  yield takeLatest(
    POWERBI_SECURITY_SYNC_PAGES.POWERBI_SECURITY_SYNC_PAGES_LOAD,
    postPowerBISecuritySyncPagesService
  );
  yield takeLatest(
    POWERBI_SECURITY_GET_REPORT_PAGES.POWERBI_SECURITY_GET_REPORT_PAGES_LOAD,
    getPowerBISecurityReportPagesService
  );
}
